import HBAvatar from "@/components/Avatar";
import HBIcon from "@/components/HBIcon";
import displayNum from "@/utils/displayNum";
import { gdImgSrcPolicy } from "@/utils/imageUrl";
import { Skeleton } from "antd";
import classNames from "classnames";
import Link from "next/link";
import { useEffect, useMemo, useRef, useState } from "react";
import styles from "./index.module.less";

export interface IHomeResourceCard {
  banner_img_url: string;
  reason: string;
  targetId: string;
  link: string;
  title: string;
  type: "board" | "user" | "link" | "topic" | "collection";
  board?: IBoardInfo;
  user?: IUser;
  explore?: {
    name: string;
    urlname: string;
  };
  name?: string;
  resource: {
    id: number;
    title: string;
  };
  contentSource: string;
  lazyImg?: boolean;
}

const resourceTypeMap = {
  board: "画板",
  user: "用户",
  link: "链接",
  topic: "专题",
  collection: "专辑",
};

export default function ResourceCard({
  banner_img_url,
  board,
  user,
  reason,
  link,
  title,
  explore,
  name,
  type,
  targetId,
  resource,
  contentSource,
  lazyImg = false,
}: IHomeResourceCard) {
  const href = useMemo(() => {
    switch (type) {
      case "board":
        return `/boards/${board?.board_id || targetId}`;
      case "user":
        return `/user/${user?.urlname}`;
      case "topic":
        return `//explore/${explore?.urlname || targetId}`;
      default:
        return link;
    }
  }, [type, board, user, explore, targetId, link]);

  const [lazyLoad, setLazyLoad] = useState(lazyImg);

  const [imageLoading, setImageLoading] = useState(true);

  const imageRef = useRef<HTMLImageElement>(null);
  // 图片在已缓存的情况下，不会触发onLoad事件，需要手动设置
  useEffect(() => {
    if (imageRef.current.complete) {
      setImageLoading(false);
    }
  }, [banner_img_url]);

  // 只有开始为 lazyLoad 为 true ，才设置为 false ，且只设置一次
  useEffect(() => {
    if (!lazyImg && lazyLoad) {
      setLazyLoad(false);
    }
  }, [lazyImg, lazyLoad]);

  return (
    <Link
      href={href}
      className={styles.card}
      title={title}
      data-gd-click="resource_loc_click"
      data-gd-expose="resource_loc_expose"
      data-resource-type={resourceTypeMap[type]}
      data-resource-id={resource?.id}
      data-resource-title={resource?.title}
      data-content-source={contentSource}
    >
      {reason && (
        <div className={styles.reason}>
          {/* <HBIcon type="ic_hot" />  */}
          {reason}
        </div>
      )}
      {imageLoading && <Skeleton.Input active className={styles.skeleton} />}
      <img
        ref={imageRef}
        width={200}
        alt={
          name ||
          board?.title ||
          user?.username ||
          explore?.name ||
          title ||
          reason
        }
        className={classNames(styles.cover, {
          [styles.hide]: imageLoading,
        })}
        onLoad={() => {
          setImageLoading(false);
        }}
        {...gdImgSrcPolicy(banner_img_url, { displayWidth: 260 })}
        loading={lazyLoad ? "lazy" : "eager"}
      />
      {user && (
        <div className={styles.mask}>
          <div className={styles.info}>
            <div className={styles.title}>
              <HBAvatar
                user={user}
                shape={"circle"}
                size={20}
                className={styles.avatar}
              />
              <span title={name || user.username}>{name || user.username}</span>
            </div>
            <div className={styles.number}>{`${displayNum(
              user.pin_count
            )}采集 ${displayNum(user.follower_count)}粉丝`}</div>
          </div>
        </div>
      )}
      {board && (
        <div className={styles.mask}>
          <div className={styles.info}>
            <div className={styles.title}>
              <HBIcon type="dashboard" />
              <span title={name || board.title}>{name || board.title}</span>
            </div>

            <div className={styles.number}>{`${displayNum(
              board.pin_count
            )}采集 ${displayNum(board.follow_count)}粉丝`}</div>
          </div>
        </div>
      )}
      {explore && (
        <div className={styles.mask}>
          <div className={styles.info}>
            <div className={styles.title}>
              <HBIcon type="ic_tag" />
              <span title={name || explore.name}>{name || explore.name}</span>
            </div>
          </div>
        </div>
      )}
      {(type === "link" || type === "collection") && name && (
        <div className={styles.mask}>
          <div className={styles.info}>
            <div className={styles.title}>
              <HBIcon type="ic_tag" />
              <span title={name}>{name}</span>
            </div>
          </div>
        </div>
      )}
    </Link>
  );
}
